import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Copy, Check, RefreshCw, Webhook, Zap, CreditCard, AlertTriangle, Loader2, Trash2, Upload, FileSpreadsheet, BarChart3 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FastAPI returns 4xx `detail` as either a string OR a structured object
 * (e.g. `{code: 'subscription_required', message: '…'}` for 402s). Passing
 * that object straight into `toast.error()` crashes sonner because React
 * can't render a plain object as a child. This helper normalises both
 * shapes so toasts never bring down the surrounding card.
 */
function apiErrorMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || d.code || fallback;
  return fallback;
}

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={copy}
      className="h-9 px-3 border-white/10 hover:bg-white/5 flex-shrink-0"
      data-testid={testid}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function Card({ icon: Icon, title, subtitle, accent = "red", children, testid }) {
  const accentMap = {
    red: "text-red-400 border-red-500/30 bg-red-500/10",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/10",
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${accentMap[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-white font-semibold">{title}</h3>
          <p className="text-zinc-400 text-sm mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-zinc-500 mt-1.5">{hint}</p>}
    </div>
  );
}

export function IntegrationsScreen() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [stripeSecret, setStripeSecret] = useState("");
  const [savingStripe, setSavingStripe] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/integrations/config`);
      setCfg(data);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't load integrations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (!window.confirm("Regenerate your webhook token? Any active Zapier/Make integrations will stop working until you paste the new token in.")) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/integrations/config/regenerate-token`);
      setCfg(data);
      toast.success("New webhook token generated.");
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't regenerate token."));
    } finally {
      setRegenerating(false);
    }
  };

  const saveStripeSecret = async () => {
    if (!stripeSecret.startsWith("whsec_")) {
      toast.error("Webhook secret should start with whsec_");
      return;
    }
    setSavingStripe(true);
    try {
      await axios.post(`${API}/integrations/config/stripe-secret`, { stripe_webhook_secret: stripeSecret });
      toast.success("Stripe webhook secret saved.");
      setStripeSecret("");
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't save Stripe secret."));
    } finally {
      setSavingStripe(false);
    }
  };

  const clearStripeSecret = async () => {
    if (!window.confirm("Remove the Stripe webhook secret? VidROI will stop ingesting sales from your Stripe account.")) return;
    try {
      await axios.delete(`${API}/integrations/config/stripe-secret`);
      toast.success("Stripe integration disconnected.");
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't disconnect."));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading integrations…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-screen">
      <div>
        <h1 className="text-3xl font-bold text-white">Integrations</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">
          Skip the JS pixel — push sales straight into VidROI from Stripe, Shopify, or any platform that supports webhooks. Useful when you can't install JavaScript on your checkout page.
        </p>
      </div>

      {/* Generic Zapier / Make / cURL ingest */}
      <Card
        icon={Zap}
        title="Generic webhook (Zapier, Make, n8n, cURL)"
        subtitle="Paste this URL + token into any tool that POSTs JSON on a sale."
        accent="amber"
        testid="card-generic-webhook"
      >
        <Field label="Webhook URL" hint="POST JSON to this URL with the `X-VidROI-Token` header.">
          <div className="flex gap-2">
            <Input value={cfg?.webhook_url || ""} readOnly className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200" data-testid="webhook-url" />
            <CopyButton value={cfg?.webhook_url || ""} testid="copy-webhook-url" />
          </div>
        </Field>

        <Field label="X-VidROI-Token" hint="This is the password — never paste it into a public page. Regenerate it if you suspect it leaked.">
          <div className="flex gap-2">
            <Input value={cfg?.webhook_token || ""} readOnly className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200" data-testid="webhook-token" />
            <CopyButton value={cfg?.webhook_token || ""} testid="copy-webhook-token" />
            <Button
              size="sm"
              variant="outline"
              onClick={regenerate}
              disabled={regenerating}
              className="h-9 px-3 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 flex-shrink-0"
              data-testid="regen-token"
            >
              {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </Field>

        <Field label="Required JSON body" hint="Set `utm_campaign` to the YouTube video_id you originally tracked the click against — that's the attribution key.">
          <pre className="text-xs font-mono bg-black/60 border border-white/10 rounded-md p-3 text-zinc-300 overflow-x-auto">{`{
  "utm_campaign": "dQw4w9WgXcQ",   // YouTube video_id
  "sale_amount": 29.00,
  "transaction_id": "ord_12345",   // optional
  "customer_email": "x@y.com"      // optional
}`}</pre>
        </Field>
      </Card>

      {/* Stripe direct ingest */}
      <Card
        icon={CreditCard}
        title="Stripe direct integration"
        subtitle="Connect VidROI to YOUR Stripe account (not the one you use to pay for VidROI). Listens for checkout.session.completed."
        accent={cfg?.stripe_secret_configured ? "emerald" : "red"}
        testid="card-stripe-webhook"
      >
        <ol className="text-sm text-zinc-300 space-y-2 list-decimal pl-5">
          <li>In your Stripe dashboard → Developers → Webhooks → <strong>Add endpoint</strong>.</li>
          <li>
            <strong>Endpoint URL</strong>:
            <div className="mt-1.5 flex gap-2">
              <Input value={cfg?.stripe_webhook_url || ""} readOnly className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200" data-testid="stripe-webhook-url" />
              <CopyButton value={cfg?.stripe_webhook_url || ""} testid="copy-stripe-url" />
            </div>
          </li>
          <li><strong>Events</strong>: select <code className="text-red-300">checkout.session.completed</code></li>
          <li>Save → reveal the signing secret (<code className="text-red-300">whsec_…</code>) and paste it below.</li>
          <li>
            On every Stripe Checkout Session, include the YouTube <code className="text-red-300">video_id</code> in <code className="text-red-300">metadata.utm_campaign</code> or <code className="text-red-300">client_reference_id</code> so VidROI can attribute the sale.
          </li>
        </ol>

        {cfg?.stripe_secret_configured ? (
          <div className="flex items-center justify-between rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Check className="w-4 h-4" /> Stripe webhook secret saved — VidROI is listening for sales.
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={clearStripeSecret}
              className="h-8 px-2.5 border-red-500/30 text-red-300 hover:bg-red-500/10"
              data-testid="clear-stripe-secret"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
            </Button>
          </div>
        ) : (
          <Field label="Stripe signing secret (whsec_…)" hint="VidROI verifies every Stripe webhook with this secret. Never shared, never logged.">
            <div className="flex gap-2">
              <Input
                value={stripeSecret}
                onChange={(e) => setStripeSecret(e.target.value)}
                placeholder="whsec_..."
                className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
                data-testid="stripe-secret-input"
                type="password"
              />
              <Button
                onClick={saveStripeSecret}
                disabled={savingStripe || !stripeSecret}
                className="bg-red-600 hover:bg-red-500 text-white flex-shrink-0"
                data-testid="save-stripe-secret"
              >
                {savingStripe ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
              </Button>
            </div>
          </Field>
        )}
      </Card>

      <CsvImportCard />

      <Ga4Card />

      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-5 flex gap-3" data-testid="integrations-security-note">
        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-zinc-300">
          <strong className="text-amber-300">Heads up:</strong> Treat the webhook token and Stripe secret like passwords. If either leaks, regenerate / disconnect immediately. VidROI will never email you asking for these values.
        </div>
      </div>
    </div>
  );
}


/**
 * CSV bulk-import card — drag/drop or click to upload a CSV of historical
 * sales (Shopify / Stripe / Gumroad export). Server is the source of truth
 * for validation; we just preview the result and surface errors per row.
 *
 * Required columns: utm_campaign, sale_amount
 * Optional       : transaction_id, customer_email, timestamp, utm_source,
 *                  utm_medium, currency
 */
function CsvImportCard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const pickFile = () => inputRef.current?.click();

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please drop a .csv file.");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/integrations/sales/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} sale${data.imported === 1 ? "" : "s"}.`);
      } else if (data.skipped_duplicates > 0) {
        toast.info(`All ${data.skipped_duplicates} rows were duplicates — nothing new imported.`);
      } else {
        toast.error("Nothing imported — check the errors below.");
      }
    } catch (err) {
      toast.error(apiErrorMsg(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card
      icon={FileSpreadsheet}
      title="Backfill historical sales (CSV import)"
      subtitle="Upload a Shopify / Stripe / Gumroad export to load past sales into your dashboard. One-time backfill — perfect to kill the empty-dashboard cold start."
      accent="emerald"
      testid="card-csv-import"
    >
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={pickFile}
        className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer p-8 text-center
          ${dragOver
            ? "border-emerald-400 bg-emerald-500/10"
            : "border-white/15 bg-black/30 hover:border-white/30 hover:bg-white/[0.03]"}`}
        data-testid="csv-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid="csv-file-input"
        />
        <Upload className="w-7 h-7 text-zinc-500 mx-auto mb-2" />
        {file ? (
          <div className="text-sm">
            <div className="text-white font-medium" data-testid="csv-filename">{file.name}</div>
            <div className="text-zinc-500 text-xs mt-1">{(file.size / 1024).toFixed(1)} KB · Click to choose a different file</div>
          </div>
        ) : (
          <div className="text-sm text-zinc-300">
            <span className="text-white font-medium">Click to choose</span> or drop a <code className="text-emerald-300">.csv</code> file here
            <div className="text-zinc-500 text-xs mt-1.5">Required columns: <code>utm_campaign</code>, <code>sale_amount</code> — up to 10,000 rows per upload</div>
          </div>
        )}
      </div>

      <details className="text-xs text-zinc-500 mt-2">
        <summary className="cursor-pointer hover:text-zinc-300 select-none">Example format</summary>
        <pre className="bg-black/60 border border-white/10 rounded-md p-3 mt-2 font-mono text-zinc-300 overflow-x-auto">{`utm_campaign,sale_amount,transaction_id,customer_email,timestamp
dQw4w9WgXcQ,29.00,ord_001,buyer@example.com,2026-01-15T10:00:00Z
abc123xyz,89.00,ord_002,,2026-02-01T09:30:00Z`}</pre>
      </details>

      <div className="flex justify-end">
        <Button
          onClick={upload}
          disabled={!file || uploading}
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="csv-upload-btn"
        >
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Importing…</>
          ) : (
            <><Upload className="w-3.5 h-3.5 mr-1.5" /> Import sales</>
          )}
        </Button>
      </div>

      {result && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-4 space-y-3" data-testid="csv-result">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <span className="text-emerald-300"><Check className="w-3.5 h-3.5 inline mr-1" />{result.imported} imported</span>
            <span className="text-zinc-400">{result.skipped_duplicates} duplicates skipped</span>
            <span className="text-zinc-400">{result.total_rows} total rows</span>
            {result.errors?.length > 0 && (
              <span className="text-amber-300"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{result.errors.length} issue{result.errors.length === 1 ? "" : "s"}</span>
            )}
          </div>

          {result.sample_preview?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Imported preview</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-1.5 pr-3">Row</th>
                      <th className="text-left py-1.5 pr-3">utm_campaign</th>
                      <th className="text-right py-1.5 pr-3">amount</th>
                      <th className="text-left py-1.5 pr-3">transaction</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-300">
                    {result.sample_preview.map((row) => (
                      <tr key={row.row} className="border-b border-white/[0.05] last:border-b-0">
                        <td className="py-1.5 pr-3 text-zinc-500">{row.row}</td>
                        <td className="py-1.5 pr-3 font-mono">{row.utm_campaign}</td>
                        <td className="py-1.5 pr-3 text-right">${row.sale_amount?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3 font-mono text-zinc-500">{row.transaction_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.errors?.length > 0 && (
            <details>
              <summary className="text-xs text-amber-300 cursor-pointer select-none">View {result.errors.length} issue{result.errors.length === 1 ? "" : "s"}</summary>
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-amber-500/20 bg-amber-500/[0.04] p-2">
                {result.errors.map((err, i) => (
                  <div key={`${err.row}-${err.transaction_id || i}`} className="text-[11px] font-mono text-zinc-300 py-0.5">
                    <span className="text-zinc-500">row {err.row}</span>
                    {err.transaction_id && <span className="text-zinc-500"> · {err.transaction_id}</span>}
                    <span className="text-amber-300"> — {err.error}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}



/**
 * GA4 (Google Analytics 4) mirror card.
 *
 * Lets the creator paste their GA4 Measurement ID + API Secret so every
 * VidROI-attributed sale gets duplicated to GA4 as a `purchase` event.
 * The API secret is write-only — backend never echoes it back, so once
 * saved we just show "configured" and a Disconnect button.
 */
function Ga4Card() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [measurementId, setMeasurementId] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/integrations/ga4`);
      setCfg(data);
      setMeasurementId(data.measurement_id || "");
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't load GA4 config."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const mid = measurementId.trim();
    const secret = apiSecret.trim();
    if (!mid || !mid.startsWith("G-")) {
      toast.error("Measurement ID must start with G-");
      return;
    }
    if (!secret && !cfg?.configured) {
      toast.error("Paste your GA4 API Secret to finish connecting.");
      return;
    }
    setSaving(true);
    try {
      const body = { measurement_id: mid };
      if (secret) body.api_secret = secret;
      await axios.post(`${API}/integrations/ga4`, body);
      toast.success("GA4 connected — sales will mirror as purchase events.");
      setApiSecret("");
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't save GA4 config."));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect GA4? Sales will stop mirroring to Google Analytics. You can reconnect anytime.")) return;
    try {
      await axios.delete(`${API}/integrations/ga4`);
      toast.success("GA4 disconnected.");
      setMeasurementId("");
      setApiSecret("");
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Couldn't disconnect."));
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-center text-zinc-400" data-testid="card-ga4-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading GA4…
      </div>
    );
  }

  return (
    <Card
      icon={BarChart3}
      title="Google Analytics 4 (GA4)"
      subtitle="Mirror every VidROI-attributed sale to GA4 as a `purchase` event so it shows up in your existing funnel + audience reports."
      accent={cfg?.configured ? "emerald" : "amber"}
      testid="card-ga4"
    >
      <ol className="text-sm text-zinc-300 space-y-2 list-decimal pl-5">
        <li>GA4 → Admin → Data Streams → pick your web stream → copy the <strong>Measurement ID</strong> (looks like <code className="text-amber-300">G-XXXXXXX</code>).</li>
        <li>Same stream → <strong>Measurement Protocol API secrets</strong> → <strong>Create</strong> → copy the secret value.</li>
        <li>Paste both below and save. From then on, every sale VidROI attributes also lands in GA4.</li>
      </ol>

      {cfg?.configured ? (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5 space-y-2" data-testid="ga4-connected-state">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Check className="w-4 h-4" />
              GA4 connected — Measurement ID&nbsp;
              <code className="font-mono text-emerald-200">{cfg.measurement_id}</code>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={disconnect}
              className="h-8 px-2.5 border-red-500/30 text-red-300 hover:bg-red-500/10"
              data-testid="ga4-disconnect"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
            </Button>
          </div>
          <div className="text-xs text-zinc-400 pl-6">Need to rotate the API secret? Paste a new one below and click Update.</div>
          <div className="grid sm:grid-cols-2 gap-2 pt-1">
            <Input
              value={measurementId}
              onChange={(e) => setMeasurementId(e.target.value)}
              placeholder="G-XXXXXXX"
              className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
              data-testid="ga4-measurement-id-input"
            />
            <div className="flex gap-2">
              <Input
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="New API secret (optional)"
                type="password"
                className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
                data-testid="ga4-api-secret-input"
              />
              <Button
                onClick={save}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-500 text-white flex-shrink-0"
                data-testid="ga4-update-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Update"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Measurement ID" hint="From GA4 → Admin → Data Streams.">
            <Input
              value={measurementId}
              onChange={(e) => setMeasurementId(e.target.value)}
              placeholder="G-XXXXXXX"
              className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
              data-testid="ga4-measurement-id-input"
            />
          </Field>
          <Field label="API Secret" hint="Write-only. Stored encrypted, never shown again.">
            <div className="flex gap-2">
              <Input
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="paste secret value"
                type="password"
                className="bg-black/40 border-white/10 font-mono text-xs text-zinc-200"
                data-testid="ga4-api-secret-input"
              />
              <Button
                onClick={save}
                disabled={saving || !measurementId || !apiSecret}
                className="bg-red-600 hover:bg-red-500 text-white flex-shrink-0"
                data-testid="ga4-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Connect"}
              </Button>
            </div>
          </Field>
        </div>
      )}
    </Card>
  );
}
